import { isTuningCommand, isTuningIdentity, isTuningReceipt, isTuningSettingReceipt } from './identity';
import type { TuningCommand, TuningIdentity, TuningReceipt, TuningSettingReceipt } from './identity';
import { isTuningTelemetry, TUNING_CONTROL_MAX_BYTES, TUNING_TELEMETRY_MAX_BYTES } from './measurements';
import type { TuningTelemetryWindow } from './measurements';
import { isResolvedTuningConfiguration, isTuningCapabilities, isTuningConfigurationHash, TUNING_STRATEGY_IDS } from './settings';
import type { ResolvedTuningConfiguration, TuningCapabilities } from './settings';
import { isTuningExperimentRequest, isTuningExperimentSnapshot } from './experiments';
import type { TuningExperimentRequest, TuningExperimentSnapshot } from './experiments';
import { hasOnlyTuningKeys, isTuningArray, isTuningError, isTuningInteger, isTuningNumber,
  isTuningRecord, isTuningString, tuningJsonBytes } from './validation';

export type TuningTrialControlContext = {
  target: TuningIdentity; commandId: string; leaseId: string;
  configurationHash: string; deadlineHostMs: number; strategyId: string;
};
export type TuningPrepare = TuningTrialControlContext & { resolvedConfiguration: ResolvedTuningConfiguration };
export type TuningCommit = TuningTrialControlContext;
export type TuningSessionGrant = {
  target: TuningIdentity; leaseId: string; leaseTtlMs: number; production: ResolvedTuningConfiguration;
};
export type TuningSessionReady = { target: TuningIdentity; leaseId: string; capabilities: TuningCapabilities };
export type TuningPrepared = TuningTrialControlContext & {
  accepted: boolean; settings: readonly TuningSettingReceipt[]; error?: { code: string; message: string };
};
export type TuningResume = TuningTrialControlContext & {
  stage: 'connected' | 'first-frame'; settings: readonly TuningSettingReceipt[];
  firstFrame?: { guestConnectionStartMonotonicMs: number; guestCompositionMonotonicMs: number; guestEndpointConnectionEpoch: number };
};
export type TuningAbort = TuningTrialControlContext & { reason: string };
export type TuningExperimentControl =
  | { action: 'start'; request: TuningExperimentRequest }
  | { action: 'status'; requestId: string; sessionId: string; leaseId: string; experimentId: string }
  | { action: 'cancel'; requestId: string; sessionId: string; leaseId: string; experimentId: string; reason: string };
export type TuningExperimentState = {
  requestId: string; sessionId: string; snapshot?: TuningExperimentSnapshot; error?: { code: string; message: string };
};
export type TuningWireMessage = { type: 'stream-tuning'; version: 1 } & (
  | { kind: 'hello'; payload: TuningCapabilities }
  | { kind: 'session'; payload: TuningSessionGrant }
  | { kind: 'session-ready'; payload: TuningSessionReady }
  | { kind: 'command'; payload: TuningCommand }
  | { kind: 'receipt'; payload: TuningReceipt }
  | { kind: 'telemetry'; payload: TuningTelemetryWindow }
  | { kind: 'prepare'; payload: TuningPrepare }
  | { kind: 'prepared'; payload: TuningPrepared }
  | { kind: 'commit'; payload: TuningCommit }
  | { kind: 'resume'; payload: TuningResume }
  | { kind: 'abort'; payload: TuningAbort }
  | { kind: 'experiment-command'; payload: TuningExperimentControl }
  | { kind: 'experiment-state'; payload: TuningExperimentState }
);
const contextKeys = ['target', 'commandId', 'leaseId', 'configurationHash', 'deadlineHostMs', 'strategyId'];

export function isTuningSessionGrant(value: unknown): value is TuningSessionGrant {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['target', 'leaseId', 'leaseTtlMs', 'production'])
    && isTuningIdentity(value['target']) && isTuningString(value['leaseId'])
    && isTuningInteger(value['leaseTtlMs'], 1_000, 900_000) && isResolvedTuningConfiguration(value['production']);
}
export function isTuningSessionReady(value: unknown): value is TuningSessionReady {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['target', 'leaseId', 'capabilities'])
    && isTuningIdentity(value['target']) && isTuningString(value['leaseId']) && isTuningCapabilities(value['capabilities'])
    && value['capabilities'].endpoint === 'guest';
}

export function isTuningTrialControlContext(value: unknown): value is TuningTrialControlContext & Record<string, unknown> {
  return isTuningRecord(value) && isTuningIdentity(value['target']) && isTuningString(value['commandId']) &&
    isTuningString(value['leaseId']) && isTuningConfigurationHash(value['configurationHash']) &&
    isTuningNumber(value['deadlineHostMs']) && isTuningString(value['strategyId']) && TUNING_STRATEGY_IDS.includes(value['strategyId']);
}

export function isTuningPrepare(value: unknown): value is TuningPrepare {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, [...contextKeys, 'resolvedConfiguration']) &&
    isTuningTrialControlContext(value) && isResolvedTuningConfiguration(value['resolvedConfiguration']) &&
    value['resolvedConfiguration'].configurationHash === value['configurationHash'] && value['resolvedConfiguration'].strategyId === value['strategyId'];
}

export function isTuningPrepared(value: unknown): value is TuningPrepared {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, [...contextKeys, 'accepted', 'settings', 'error']) &&
    isTuningTrialControlContext(value) && typeof value['accepted'] === 'boolean' && isTuningArray(value['settings'], 25, isTuningSettingReceipt) &&
    (value['error'] === undefined || isTuningError(value['error'])) && !(value['accepted'] && value['error'] !== undefined);
}

export function isTuningCommit(value: unknown): value is TuningCommit {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, contextKeys) && isTuningTrialControlContext(value);
}

export function isTuningResume(value: unknown): value is TuningResume {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, [...contextKeys, 'stage', 'settings', 'firstFrame']) ||
    !isTuningTrialControlContext(value) || !isTuningArray(value['settings'], 25, isTuningSettingReceipt)) return false;
  if (value['stage'] === 'connected') return value['firstFrame'] === undefined;
  const frame = value['firstFrame'];
  return value['stage'] === 'first-frame' && isTuningRecord(frame) && hasOnlyTuningKeys(frame,
    ['guestConnectionStartMonotonicMs', 'guestCompositionMonotonicMs', 'guestEndpointConnectionEpoch']) &&
    isTuningNumber(frame['guestConnectionStartMonotonicMs']) &&
    isTuningNumber(frame['guestCompositionMonotonicMs'], frame['guestConnectionStartMonotonicMs']) &&
    isTuningInteger(frame['guestEndpointConnectionEpoch']);
}

export function isTuningAbort(value: unknown): value is TuningAbort {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, [...contextKeys, 'reason']) &&
    isTuningTrialControlContext(value) && isTuningString(value['reason'], 2_048);
}

export function isTuningExperimentControl(value: unknown): value is TuningExperimentControl {
  if (!isTuningRecord(value)) return false;
  if (value['action'] === 'start') return hasOnlyTuningKeys(value, ['action', 'request']) && isTuningExperimentRequest(value['request']);
  const base = ['action', 'requestId', 'sessionId', 'leaseId', 'experimentId'];
  return (value['action'] === 'status' || value['action'] === 'cancel') &&
    hasOnlyTuningKeys(value, value['action'] === 'cancel' ? [...base, 'reason'] : base) &&
    ['requestId', 'sessionId', 'leaseId', 'experimentId'].every((key) => isTuningString(value[key])) &&
    (value['action'] !== 'cancel' || isTuningString(value['reason'], 2_048));
}

export function isTuningExperimentState(value: unknown): value is TuningExperimentState {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['requestId', 'sessionId', 'snapshot', 'error']) &&
    isTuningString(value['requestId']) && isTuningString(value['sessionId']) &&
    (value['snapshot'] !== undefined || value['error'] !== undefined) &&
    (value['snapshot'] === undefined || (isTuningExperimentSnapshot(value['snapshot']) && value['snapshot'].sessionId === value['sessionId'])) &&
    (value['error'] === undefined || isTuningError(value['error']));
}

export function parseTuningWireMessage(value: unknown): TuningWireMessage | undefined {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['type', 'version', 'kind', 'payload']) ||
    value['type'] !== 'stream-tuning' || value['version'] !== 1 ||
    tuningJsonBytes(value) > (value['kind'] === 'telemetry' ? TUNING_TELEMETRY_MAX_BYTES : TUNING_CONTROL_MAX_BYTES)) return undefined;
  const guards: Record<string, (payload: unknown) => boolean> = {
    session: isTuningSessionGrant, 'session-ready': isTuningSessionReady,
    hello: isTuningCapabilities, command: isTuningCommand, receipt: isTuningReceipt, telemetry: isTuningTelemetry,
    prepare: isTuningPrepare, prepared: isTuningPrepared, commit: isTuningCommit, resume: isTuningResume, abort: isTuningAbort,
    'experiment-command': isTuningExperimentControl, 'experiment-state': isTuningExperimentState,
  };
  if (typeof value['kind'] !== 'string' || !Object.prototype.hasOwnProperty.call(guards, value['kind']) || !guards[value['kind']](value['payload'])) return undefined;
  return value as TuningWireMessage;
}

export function isTuningWireMessage(value: unknown): value is TuningWireMessage {
  return parseTuningWireMessage(value) !== undefined;
}
